import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AppSetting,
  AppSettingList,
  CreateAppSettingInput,
  UpdateAppSettingInput,
  CreateAppSettingListInput,
  UpdateAppSettingListInput,
} from '@peles/shared';
import * as appSettingsApi from '../api/app-settings';

// ─── App Settings ───────────────────────────────────────────────────────────

/**
 * A change to any setting refreshes the list Settings shows and the single
 * values screens read through `useAppSettingValue` — otherwise a page opened
 * before the change keeps the old value for its five-minute stale time.
 */
function invalidateAppSettings(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['settings', 'appSettings'] });
  queryClient.invalidateQueries({ queryKey: ['appSettingValue'] });
}

export function useAppSettings() {
  return useQuery<AppSetting[]>({
    queryKey: ['settings', 'appSettings'],
    queryFn: () => appSettingsApi.fetchAppSettings(),
  });
}

export function useCreateAppSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAppSettingInput) => appSettingsApi.createAppSetting(data),
    onSuccess: () => invalidateAppSettings(queryClient),
  });
}

export function useUpdateAppSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateAppSettingInput }) =>
      appSettingsApi.updateAppSetting(id, updates),
    onSuccess: () => invalidateAppSettings(queryClient),
  });
}

export function useDeleteAppSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => appSettingsApi.deleteAppSetting(id),
    onSuccess: () => invalidateAppSettings(queryClient),
  });
}

// ─── App Setting Lists ──────────────────────────────────────────────────────

export function useAppSettingLists() {
  return useQuery<AppSettingList[]>({
    queryKey: ['settings', 'appSettingLists'],
    queryFn: () => appSettingsApi.fetchAppSettingLists(),
  });
}

export function useCreateAppSettingList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAppSettingListInput) => appSettingsApi.createAppSettingList(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'appSettingLists'] });
    },
  });
}

export function useUpdateAppSettingList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateAppSettingListInput }) =>
      appSettingsApi.updateAppSettingList(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'appSettingLists'] });
    },
  });
}

export function useDeleteAppSettingList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => appSettingsApi.deleteAppSettingList(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'appSettingLists'] });
    },
  });
}
